import { sdkRequest } from '@eventbrite/http';
import { formatUrl } from 'url-lib';
import { IBrowseFilters } from '../../../../constants/filters';
import { bboxShape } from '../../../../types/index';

const EVENT_EXPANSIONS = [
    'primary_venue',
    'image',
    'ticket_availability',
    'saves',
    'event_sales_status',
    'primary_organizer',
    'public_collections',
];

export const getUserSavedEvents = () => {
    const url = formatUrl('/api/v3/destination/saved/', {
        'expand.destination_event': EVENT_EXPANSIONS,
        page_size: 4,
    });

    return sdkRequest(url);
};

/**
 * This method returns events from organizers the user is following.
 */
export const getFollowedProfiles = () => {
    const url = formatUrl('/api/v3/destination/search/');
    const maxEvents = 16;

    return sdkRequest(url, {
        method: 'POST',
        body: JSON.stringify({
            event_search: {
                dates: ['current_future'],
                page: 1,
                image: true,
                special: ['organizers_i_follow'],
                sort: 'date',
                page_size: maxEvents,
            },
            'expand.destination_event': EVENT_EXPANSIONS,
        }),
    });
};

// This endpoint returns organizers the users follows
export const getUserFollowing = (userId: string) => {
    const url = formatUrl(`/api/v3/destination/users/${userId}/following/`);

    return sdkRequest(url);
};

/*
 * Users can optionally apply filters (ie. Date) to personalized content.
 * buildPayload parses the filters into either data (filterData) or
 * payload params (filterParams) for the recommendations API endpoint
 */

const buildPayload = (filters?: IBrowseFilters[]) => {
    const payload = filters?.reduce(
        (prev, curr) => {
            let filterParams = {};
            let filterData = {};

            if (curr.payload === 'param') {
                filterParams = {
                    ...prev.filterParams,
                    [curr.name as string]: curr.value,
                };
            } else if (curr.payload === 'data') {
                filterData = {
                    ...prev.filterData,
                    [curr.name as string]: curr.value,
                };
            }
            return {
                filterParams,
                filterData,
            };
        },
        {
            filterParams: {},
            filterData: {},
        },
    );

    return payload;
};

// This endpoint returns events and organizers personalized for the user
export const getUserPersonalizedContent = async (
    place_id?: string | null,
    locale?: string | null,
    prevSearches?: string[],
    filters?: Array<object>,
    views?: string | null,
) => {
    const EVENT_EXPANSIONS =
        'event_sales_status,image,primary_venue,saves,series,ticket_availability,primary_organizer';
    const PROFILE_EXPANSIONS = 'image';

    const params = {
        'expand.destination_event': EVENT_EXPANSIONS,
        'expand.destination_profile': PROFILE_EXPANSIONS,
        place_id: place_id,
        locale: locale,
        user_recent_searches: prevSearches,
        user_recent_views: views,
    };

    const payload = buildPayload(filters);
    let postData = {};

    const url = formatUrl(
        '/api/v3/destination/users/me/recommendations/my_feed_recommendations_all/',
        {
            ...params,
            ...payload?.filterParams,
        },
    );

    if (payload?.filterData && JSON.stringify(payload?.filterData) !== '{}') {
        postData = {
            method: 'POST',
            body: JSON.stringify(payload.filterData),
        };
    }

    const personalizedContent = await sdkRequest(url, postData);

    return personalizedContent;
};

// This endpoint returns personalized event recommendations
export const getUserEventRecommendations = async () => {
    const recommendedEventIds = await sdkRequest(
        '/api/v3/destination/users/me/recommendations/my_offline_events_recommendation/',
    );
    let recommendedEvents = [];

    if (recommendedEventIds.my_offline_events_recommendation.length) {
        const inflateUrl = formatUrl(`/api/v3/destination/events/`, {
            'expand.destination_event': EVENT_EXPANSIONS,
            event_ids:
                recommendedEventIds.my_offline_events_recommendation.join(','),
        });
        recommendedEvents = await sdkRequest(inflateUrl);
    }

    return recommendedEvents;
};

export const fetchCityBrowseEvents = (placeId: string) => {
    const url = formatUrl(`/api/v3/destination/city-browse/${placeId}/`, {
        'expand.destination_event': EVENT_EXPANSIONS,
        buckets_list: 'best_of_city_events',
    });

    return sdkRequest(url);
};

export const searchEvents = (placeId: string) => {
    const url = formatUrl('/api/v3/destination/search/');

    return sdkRequest(url, {
        method: 'POST',
        body: JSON.stringify({
            event_search: {
                places: [placeId],
                dates: ['current_future'],
                page_size: 30,
                image: true,
            },
            'expand.destination_event': EVENT_EXPANSIONS,
        }),
    });
};

export const searchEventsWithParameters = ({
    placeId,
    bbox,
    eventSearch,
    isOnline,
}: {
    placeId: string;
    bbox: bboxShape;
    eventSearch: {
        dates: string[];
        tags: string;
    };
    isOnline: boolean;
}) => {
    const url = formatUrl('/api/v3/destination/search/');

    let searchOptions: any = {
        places: [placeId],
    };

    // sometimes google maps autocomplete returns with a lat lng that we can't
    // translate to a place id. In that case, we resort to sending a bounding box.
    if (!placeId && bbox) {
        searchOptions = {
            bbox: `${bbox.sw?.lng},${bbox.sw?.lat},${bbox.ne?.lng},${bbox.ne?.lat}`,
        };
    }

    // Let's default to an online search if place or bbox still are not present.
    if (isOnline || (!placeId && !bbox)) {
        searchOptions = {
            online_events_only: true,
        };
    }

    return sdkRequest(url, {
        method: 'POST',
        body: JSON.stringify({
            event_search: {
                page_size: 30,
                image: true,
                ...searchOptions,
                ...eventSearch,
            },
            'expand.destination_event': EVENT_EXPANSIONS,
        }),
    });
};
